/**
 * Nijhof Brothers: website-eventlaag (trackingcontract).
 * Volledige beschrijving: docs/GROWTH_OS_INTEGRATION.md
 *
 * Elke gebeurtenis gaat drie kanten op:
 *   1. window.dataLayer.push({ event: 'nb_<naam>', nb: {...}, ...payload })
 *   2. document.dispatchEvent(new CustomEvent('nb:track', { detail }))
 *   3. Shopify.analytics.publish('nb_<naam>', detail)  -> leesbaar in een Custom Pixel
 *
 * Dit bestand verstuurt zelf NIETS naar externe servers en zet geen cookies.
 * Het leest de cookie-voorkeur via Shopify.customerPrivacy en geeft die mee in elk event,
 * zodat de ontvanger (Growth OS-pixel) kan beslissen wat er mag worden doorgestuurd.
 */
const VERSION = '1.0.0';
const PREFIX = 'nb_';

function pageData() {
  try {
    const el = document.getElementById('nb-page-data');
    return el ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
}

function consent() {
  const cp = window.Shopify && window.Shopify.customerPrivacy;
  const out = { analytics: null, marketing: null, preferences: null, sale_of_data: null };
  if (!cp) return out;
  try {
    if (typeof cp.analyticsProcessingAllowed === 'function') out.analytics = cp.analyticsProcessingAllowed();
    if (typeof cp.marketingAllowed === 'function') out.marketing = cp.marketingAllowed();
    if (typeof cp.preferencesProcessingAllowed === 'function') out.preferences = cp.preferencesProcessingAllowed();
    if (typeof cp.saleOfDataAllowed === 'function') out.sale_of_data = cp.saleOfDataAllowed();
  } catch {
    /* privacy-API nog niet geladen */
  }
  return out;
}

function sessionId() {
  try {
    let id = sessionStorage.getItem('nb_sid');
    if (!id) {
      id =
        window.crypto && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now()) + '-' + Math.random().toString(16).slice(2);
      sessionStorage.setItem('nb_sid', id);
    }
    return id;
  } catch {
    return null;
  }
}

function cookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Verstuur een website-event.
 * @param {string} name - eventnaam zonder prefix, bijv. 'whatsapp_click'
 * @param {Record<string, unknown>} [payload]
 */
export function track(name, payload = {}) {
  const page = pageData();
  const c = consent();
  const detail = {
    event: PREFIX + name,
    nb: {
      version: VERSION,
      name,
      session_id: sessionId(),
      shopify_y: c.analytics ? cookie('_shopify_y') : null,
      page_type: page.page_type || null,
      template: page.template || null,
      lijn: (page.product && page.product.lijn) || null,
      url: location.href,
      path: location.pathname,
      referrer: document.referrer || null,
      ts: new Date().toISOString(),
      consent: c,
    },
    product: page.product || null,
    ...payload,
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(detail);
  document.dispatchEvent(new CustomEvent('nb:track', { detail }));
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(PREFIX + name, detail);
    }
  } catch {
    /* analytics-sandbox niet beschikbaar */
  }
  if (window.NB_DEBUG) console.debug('[nb:track]', detail);
  return detail;
}

function parsePayload(el) {
  try {
    return el.dataset.nbPayload ? JSON.parse(el.dataset.nbPayload) : {};
  } catch {
    return {};
  }
}

function bindClicks() {
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const el = target.closest('[data-nb-event]');
      if (el) {
        track(el.dataset.nbEvent, {
          ...parsePayload(el),
          label: (el.getAttribute('aria-label') || el.textContent || '').trim().slice(0, 120),
          href: el.getAttribute('href') || null,
        });
        return;
      }
      const checkout = target.closest('button[name="checkout"], a[href^="/checkout"], [data-nb-checkout]');
      if (checkout) track('checkout_click', { source: checkout.closest('cart-drawer, .cart, form[action*="/cart"]') ? 'cart' : 'page' });
    },
    { capture: true }
  );
}

function bindVariantAndCart() {
  document.addEventListener('variant:update', (event) => {
    const d = event.detail || {};
    const v = d.resource || {};
    track('variant_selected', {
      variant_id: v.id || null,
      variant_title: v.title || null,
      sku: v.sku || null,
      price: typeof v.price === 'number' ? v.price / 100 : null,
      available: typeof v.available === 'boolean' ? v.available : null,
      product_id: (d.data && d.data.productId) || null,
    });
  });

  document.addEventListener('cart:update', (event) => {
    const d = (event.detail && event.detail.data) || {};
    if (d.didError) return;
    const source = d.source || '';
    if (d.variantId && source !== 'cart-items-component') {
      track('add_to_cart', {
        variant_id: d.variantId,
        product_id: d.productId || null,
        item_count: typeof d.itemCount === 'number' ? d.itemCount : null,
        source,
      });
    } else {
      track('cart_updated', { item_count: typeof d.itemCount === 'number' ? d.itemCount : null, source });
    }
  });
}

function bindForms() {
  const started = new WeakSet();
  document.addEventListener(
    'focusin',
    (event) => {
      const form = event.target instanceof Element ? event.target.closest('form[data-nb-form]') : null;
      if (!form || started.has(form)) return;
      started.add(form);
      track(form.dataset.nbForm + '_started', parsePayload(form));
    },
    true
  );
  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !form.dataset.nbForm) return;
    const data = new FormData(form);
    const fields = [];
    for (const key of data.keys()) {
      if (!/^(form_type|utf8|authenticity_token)$/.test(key)) fields.push(key);
    }
    track(form.dataset.nbForm + '_submitted', { ...parsePayload(form), fields });
  });
}

function pageView() {
  const page = pageData();
  if (page.product) {
    track('product_viewed', {
      variant_id: page.product.variant_id || null,
      price: typeof page.product.price === 'number' ? page.product.price : null,
    });
  } else if (page.collection) {
    track('collection_viewed', { collection: page.collection });
  }
}

function init() {
  if (window.NB && window.NB.version) return;
  window.NB = { track, version: VERSION };
  bindClicks();
  bindVariantAndCart();
  bindForms();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pageView, { once: true });
  else pageView();
}

init();